'use client';

import { useEffect } from 'react';

type AppVersionInfo = {
  version: string;
  buildId: string;
  deployedAt: string;
};

declare global {
  interface Window {
    __F9_APP_VERSION__?: AppVersionInfo;
  }
}

function sameVersion(left: AppVersionInfo | undefined, right: AppVersionInfo | undefined) {
  return Boolean(
    left &&
      right &&
      left.version === right.version &&
      left.buildId === right.buildId &&
      left.deployedAt === right.deployedAt,
  );
}

async function clearBuildCaches() {
  const tasks: Promise<unknown>[] = [];
  if ('caches' in window) {
    tasks.push(
      window.caches.keys().then((keys) => Promise.all(keys.map((key) => window.caches.delete(key)))),
    );
  }
  if ('serviceWorker' in navigator) {
    tasks.push(
      navigator.serviceWorker.getRegistrations().then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister())),
      ),
    );
  }
  await Promise.allSettled(tasks);
}

async function reloadForBuild(currentVersion: AppVersionInfo) {
  const reloadKey = `f9-version-reload:${currentVersion.version}:${currentVersion.buildId}:${currentVersion.deployedAt}`;
  if (sessionStorage.getItem(reloadKey) === '1') return;

  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith('f9-version-reload:')) sessionStorage.removeItem(key);
  }
  sessionStorage.setItem(reloadKey, '1');
  await clearBuildCaches();

  const reloadUrl = new URL(window.location.href);
  reloadUrl.searchParams.set('__f9_build', currentVersion.buildId);
  window.location.replace(reloadUrl.toString());
}

export function AppVersionWatcher() {
  useEffect(() => {
    const loadedVersion = window.__F9_APP_VERSION__;
    let stopped = false;

    const checkVersion = async () => {
      try {
        const cacheBuster = Date.now();
        const [manifestResponse, apiResponse] = await Promise.all([
          fetch(`/app-version.json?t=${cacheBuster}`, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
          }),
          fetch(`/api/app-version?t=${cacheBuster}`, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
          }),
        ]);
        if (!manifestResponse.ok) return;
        const currentVersion = await manifestResponse.json() as AppVersionInfo;
        if (apiResponse.ok) {
          const apiVersion = await apiResponse.json() as AppVersionInfo;
          if (!sameVersion(currentVersion, apiVersion)) return;
        }
        if (stopped || sameVersion(loadedVersion, currentVersion)) return;
        await reloadForBuild(currentVersion);
      } catch {
        // Version checking must never interrupt normal app usage.
      }
    };

    const onFocus = () => void checkVersion();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };
    const interval = window.setInterval(() => void checkVersion(), 120000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void checkVersion();

    return () => {
      stopped = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
}
